import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Wallet, Calendar } from "lucide-react";
import { toast } from "sonner";

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export default function FlussoCassa() {
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/cash-flow/agency", { params: { data: date } });
      setData(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Liquidità</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Flusso di cassa</h1>
        <p className="text-sm text-slate-500 mt-1">Saldi giornalieri di cassa per ciascun utente abilitato al flusso (contante + assegno).</p>
      </div>

      <Card className="p-5 border-slate-200">
        <div className="flex items-end gap-3">
          <div>
            <Label>Data</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="md:w-56"
              data-testid="flusso-data-input"
            />
          </div>
        </div>
      </Card>

      {loading && <div className="text-slate-500 text-sm">Caricamento…</div>}

      {!loading && data && data.rows.length === 0 && (
        <Card className="p-10 text-center border-dashed border-slate-200" data-testid="flusso-empty">
          <Wallet className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <div className="text-slate-700 font-medium">Nessun foglio cassa con flusso attivo</div>
          <div className="text-sm text-slate-500 mt-1">
            Attiva il flusso di cassa dell'agente e/o dei collaboratori dalla pagina <strong>Abbonamento</strong> e <strong>Utenze</strong>.
          </div>
        </Card>
      )}

      {!loading && data && data.rows.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Card className="p-5 border-slate-200">
              <div className="text-xs uppercase text-slate-500 tracking-wider font-semibold">Saldo iniziale totale</div>
              <div className="font-heading text-2xl font-bold text-slate-900 font-mono-num mt-1">
                {formatEUR(data.totale_iniziale)}
              </div>
            </Card>
            <Card className="p-5 border-emerald-200 bg-emerald-50/30">
              <div className="text-xs uppercase text-emerald-700 tracking-wider font-semibold">Saldo finale totale</div>
              <div className="font-heading text-2xl font-bold text-emerald-700 font-mono-num mt-1" data-testid="flusso-totale-finale">
                {formatEUR(data.totale_finale)}
              </div>
            </Card>
          </div>

          <Card className="border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left">
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Utente</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Saldo iniziale</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">+ Incassi</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">− Abbuoni</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">− Versamenti usciti</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">+ Versamenti in entrata</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Saldo finale</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.rows.map((r) => (
                    <tr key={r.sheet_id} data-testid={`flusso-row-${r.user.role}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{r.user.label}</div>
                        <Badge variant="outline" className={
                          r.user.role === "agente" ? "bg-slate-900 text-white border-slate-900 mt-1"
                          : "bg-slate-100 text-slate-700 border-slate-200 mt-1"
                        }>
                          {r.user.role === "agente" ? "Agente" : "Collaboratore"}
                        </Badge>
                        {r.flow.overridden && (
                          <div className="text-[10px] text-amber-700 mt-1">saldo modificato manualmente</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono-num">{formatEUR(r.flow.saldo_iniziale)}</td>
                      <td className="px-4 py-3 text-right font-mono-num text-emerald-700">{formatEUR(r.flow.incassi_liquidi)}</td>
                      <td className="px-4 py-3 text-right font-mono-num text-red-600">{formatEUR(r.flow.abbuoni_liquidi)}</td>
                      <td className="px-4 py-3 text-right font-mono-num text-red-600">{formatEUR(r.flow.versamenti_uscita)}</td>
                      <td className="px-4 py-3 text-right font-mono-num text-emerald-700">
                        {r.user.role === "agente" ? formatEUR(r.flow.versamenti_entrata) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-mono-num font-semibold text-slate-900">{formatEUR(r.flow.saldo_finale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
